import { ImageIcon, ImageOff } from "lucide-react";
import { useProductImageUrl } from "@/api/inventory";

interface ProductImageProps {
  productId: string;
  hasImage: boolean;
  alt: string;
  /** Clases del contenedor cuadrado (define el tamaño). Ej: "h-12 w-12" en una fila de tabla, "h-56 w-56" en el Validador de precios. */
  className?: string;
  iconSize?: number;
}

/**
 * Muestra la foto de un producto reutilizando el mismo endpoint protegido
 * en todos lados (fila de la tabla de Productos, Validador de precios,
 * etc.) — un solo componente, sin duplicar la lógica de traer el binario
 * con el header Authorization (ver useProductImageUrl en api/inventory.ts).
 * Si el producto no tiene imagen, o falla la carga, muestra un placeholder
 * neutro en vez de un ícono roto — nunca un error técnico visible.
 */
export function ProductImage({ productId, hasImage, alt, className = "h-12 w-12", iconSize = 20 }: ProductImageProps) {
  const { data: url, isLoading, isError } = useProductImageUrl(productId, hasImage);

  if (!hasImage) {
    return (
      <div className={`flex shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-300 ${className}`}>
        <ImageIcon size={iconSize} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className={`flex shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-300 ${className}`}>
        <ImageOff size={iconSize} />
      </div>
    );
  }

  if (isLoading || !url) {
    return <div className={`shrink-0 animate-pulse rounded-lg bg-gray-100 ${className}`} />;
  }

  return <img src={url} alt={alt} className={`shrink-0 rounded-lg border border-gray-100 object-cover ${className}`} />;
}
